/**
 * TabularToolbar — same CommandBar chrome as the list: Add row locked left,
 * Group by / Columns / Fit / Import in More (pinnable).
 */
import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import AddIcon from '@mui/icons-material/Add'
import FitScreenIcon from '@mui/icons-material/FitScreen'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import { ColumnsMenu } from '../ColumnsMenu'
import { CommandBar, MORE_PIN_GRID, PinToggle } from '@/shared/components/CommandBar'
import type { CommandItem } from '@/shared/components/CommandBar'
import { prefsStore } from '@/app/preferences/prefsStore'
import {
  commandPinKey,
  parsePinPrefs,
  resolvePinSlot,
  type PinSlot,
} from '@/shared/form/commandPin'
import { GroupBySettings } from '@/shared/groupBy'

export interface TabularToolbarColumn {
  name: string
  title: string
}

const TABULAR_CHROME_IDS = ['GroupBy', 'Columns'] as const
type TabularChromeId = (typeof TABULAR_CHROME_IDS)[number]

export interface TabularToolbarProps {
  columns: TabularToolbarColumn[]
  groupBy: string[]
  onGroupByChange: (next: string[]) => void
  hidden: string[]
  onToggleColumn: (name: string) => void
  onResetColumns: () => void
  onFit: () => void
  onAddRow: () => void
  onImportExcel?: () => void
  disabled?: boolean
  connectionId: string
  objectName: string
  tabularName: string
  search: { value: string; onChange: (v: string) => void }
}

export const TabularToolbar: React.FC<TabularToolbarProps> = ({
  columns,
  groupBy,
  onGroupByChange,
  hidden,
  onToggleColumn,
  onResetColumns,
  onFit,
  onAddRow,
  onImportExcel,
  disabled = false,
  connectionId,
  objectName,
  tabularName,
  search,
}) => {
  const [pinRevision, setPinRevision] = useState(0)
  const pinKey = commandPinKey(connectionId, objectName, `Tabular:${tabularName}`)
  const pinPrefs = parsePinPrefs(pinRevision >= 0 ? prefsStore.getItem(pinKey) : null)
  const slotOf = (id: TabularChromeId): PinSlot => resolvePinSlot(id, 'overflow', pinPrefs)

  const extraCommands: CommandItem[] = [
    {
      id: 'Import',
      label: 'Import from Excel',
      icon: <FileUploadIcon />,
      onClick: () => onImportExcel?.(),
      disabled,
    },
    {
      id: 'Fit',
      label: 'Fit columns',
      icon: <FitScreenIcon />,
      onClick: () => onFit(),
    },
  ]
  const secondary: CommandItem[] = []
  const overflow: CommandItem[] = []
  for (const item of extraCommands) {
    const id = item.id ?? item.label
    if (resolvePinSlot(id, undefined, pinPrefs) === 'overflow') overflow.push(item)
    else secondary.push(item)
  }

  const handlePin = (id: string, slot: PinSlot) => {
    const current = parsePinPrefs(prefsStore.getItem(pinKey)) ?? { pinned: [], overflow: [] }
    const next =
      slot === 'bar'
        ? {
            pinned: [...current.pinned.filter((x) => x !== id), id],
            overflow: current.overflow.filter((x) => x !== id),
          }
        : {
            pinned: current.pinned.filter((x) => x !== id),
            overflow: [...current.overflow.filter((x) => x !== id), id],
          }
    prefsStore.setItem(pinKey, JSON.stringify(next))
    setPinRevision((n) => n + 1)
  }
  const handleResetPins = () => {
    prefsStore.removeItem(pinKey)
    setPinRevision((n) => n + 1)
  }

  const groupByField = columns.length > 0 ? (
    <GroupBySettings
      fields={columns}
      value={groupBy}
      onChange={onGroupByChange}
      disabled={disabled}
    />
  ) : null

  const wrapBar = (id: TabularChromeId, node: React.ReactNode) => {
    if (node == null || node === false) return null
    return (
      <Box key={id} sx={{ display: 'flex', alignItems: 'center' }}>
        {node}
      </Box>
    )
  }

  const wrapMore = (id: TabularChromeId, label: string, node: React.ReactNode) => {
    if (node == null || node === false) return null
    const pinned = slotOf(id) === 'bar'
    return (
      <Box
        key={id}
        sx={{
          ...MORE_PIN_GRID,
          width: '100%',
          px: 1.5,
          py: 0.75,
          boxSizing: 'border-box',
        }}
      >
        {pinned ? (
          <Typography sx={{ fontSize: 14 }}>{label}</Typography>
        ) : (
          <Box sx={{ minWidth: 0 }}>{node}</Box>
        )}
        <PinToggle
          label={label}
          pinned={pinned}
          onChange={(slot) => handlePin(id, slot)}
        />
      </Box>
    )
  }

  const parts: { id: TabularChromeId; label: string; barNode: React.ReactNode; moreNode: React.ReactNode }[] = [
    { id: 'GroupBy', label: 'Group by', barNode: groupByField, moreNode: groupByField },
    {
      id: 'Columns',
      label: 'Columns',
      barNode: (
        <ColumnsMenu iconOnly columns={columns} hidden={hidden} onToggle={onToggleColumn} onReset={onResetColumns} />
      ),
      moreNode: (
        <ColumnsMenu columns={columns} hidden={hidden} onToggle={onToggleColumn} onReset={onResetColumns} />
      ),
    },
  ]
  const barChrome = parts
    .filter((p) => slotOf(p.id) === 'bar')
    .map((p) => wrapBar(p.id, p.barNode))
    .filter(Boolean)
  const moreChrome = parts
    .map((p) => wrapMore(p.id, p.label, p.moreNode))
    .filter(Boolean)

  const searchField = (
    <TextField
      size="small"
      placeholder="Search..."
      value={search.value}
      onChange={(e) => search.onChange(e.target.value)}
      sx={{ width: 240 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
        ),
        endAdornment: search.value ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              aria-label="Clear search"
              onClick={() => search.onChange('')}
              edge="end"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
    />
  )

  return (
    <CommandBar
      toolbar={{ mark: 'tabular' }}
      primary={[{
        id: 'AddRow',
        label: 'Add row',
        icon: <AddIcon />,
        onClick: () => onAddRow(),
        disabled,
      }]}
      secondary={secondary}
      overflow={overflow}
      onPin={handlePin}
      onResetPins={handleResetPins}
      utilities={
        <>
          {searchField}
          {barChrome}
        </>
      }
      moreExtras={moreChrome.length > 0 ? <>{moreChrome}</> : undefined}
    />
  )
}
